import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * A self-contained SVG bar chart — no charting library, so nothing to conflict
 * with the (very new) React version and nothing extra in the bundle.
 *
 * Give it [{ label, value }] and it draws bars with a value axis, gridlines,
 * x labels (thinned out when there are many), and a hover tooltip per bar.
 * It scales to the width of its container.
 */
export interface BarDatum {
  label: string;
  value: number;
}

interface BarChartProps {
  data: BarDatum[];
  height?: number;
  color?: string;
  valuePrefix?: string; // e.g. "$"
  valueSuffix?: string;
}

function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(n)));
  const norm = n / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

function fmt(n: number, prefix: string, suffix: string): string {
  const v = Math.abs(n) >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : n.toFixed(2);
  return `${prefix}${v}${suffix}`;
}

export default function BarChart({
  data,
  height = 300,
  color = '#1976d2',
  valuePrefix = '',
  valueSuffix = '',
}: BarChartProps) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="textSecondary">No data to chart.</Typography>
      </Box>
    );
  }

  // Layout in SVG user units; the SVG then scales to the container width.
  const W = Math.max(600, Math.min(1600, data.length * 46 + 80));
  const H = height;
  const mL = 64; // left margin for value axis
  const mR = 16;
  const mT = 12;
  const mB = 64; // bottom margin for x labels
  const plotW = W - mL - mR;
  const plotH = H - mT - mB;

  const maxRaw = data.reduce((m, d) => Math.max(m, d.value), 0);
  const maxV = niceCeil(maxRaw);

  const gridCount = 4;
  const gridlines = Array.from({ length: gridCount + 1 }, (_, i) => {
    const val = (maxV / gridCount) * i;
    const y = mT + plotH - (val / maxV) * plotH;
    return { val, y };
  });

  const slot = plotW / data.length;
  const barW = Math.max(4, Math.min(48, slot * 0.62));

  // Thin x labels so they don't overlap: aim for ~16 labels max.
  const labelEvery = Math.max(1, Math.ceil(data.length / 16));

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        preserveAspectRatio="xMinYMid meet"
        role="img"
        aria-label="Bar chart"
      >
        {/* gridlines + value labels */}
        {gridlines.map((g, i) => (
          <g key={i}>
            <line x1={mL} y1={g.y} x2={W - mR} y2={g.y} stroke="#e0e0e0" strokeWidth={1} />
            <text x={mL - 8} y={g.y + 4} textAnchor="end" fontSize={11} fill="#666">
              {fmt(g.val, valuePrefix, valueSuffix)}
            </text>
          </g>
        ))}

        {/* bars */}
        {data.map((d, i) => {
          const x = mL + i * slot + (slot - barW) / 2;
          const h = maxV > 0 ? (d.value / maxV) * plotH : 0;
          const y = mT + plotH - h;
          const showLabel = i % labelEvery === 0;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={Math.max(0, h)} fill={color} rx={2}>
                <title>{`${d.label}: ${fmt(d.value, valuePrefix, valueSuffix)}`}</title>
              </rect>
              {showLabel && (
                <text
                  x={mL + i * slot + slot / 2}
                  y={mT + plotH + 14}
                  textAnchor="end"
                  fontSize={10}
                  fill="#666"
                  transform={`rotate(-40 ${mL + i * slot + slot / 2} ${mT + plotH + 14})`}
                >
                  {d.label}
                </text>
              )}
            </g>
          );
        })}

        {/* axes */}
        <line x1={mL} y1={mT} x2={mL} y2={mT + plotH} stroke="#bdbdbd" strokeWidth={1} />
        <line x1={mL} y1={mT + plotH} x2={W - mR} y2={mT + plotH} stroke="#bdbdbd" strokeWidth={1} />
      </svg>
    </Box>
  );
}
